import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, Phone, Send, CheckCircle2, Loader2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => { if (!cancelled && ctx?.organizations?.[0]?.id) { const id = ctx.organizations[0].id; setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(orgId ? { 'X-Organization-Id': orgId } : {}) }), [token, orgId]);
  return { headers, orgId };
};

const PROVIDERS = [
  { key: 'wa_twilio', title: 'WhatsApp — Twilio', icon: MessageSquare, integration_type: 'messaging', provider: 'twilio',
    fields: [['account_sid', 'Account SID'], ['auth_token', 'Auth Token', true], ['from_number', 'WhatsApp-enabled number (+…)']] },
  { key: 'voice_twilio', title: 'Voice — Twilio', icon: Phone, integration_type: 'voice', provider: 'twilio',
    fields: [['account_sid', 'Account SID'], ['auth_token', 'Auth Token', true], ['from_number', 'Voice-enabled number (+…)']] },
  { key: 'wa_meta', title: 'WhatsApp — Meta Cloud', icon: MessageSquare, integration_type: 'messaging', provider: 'whatsapp_cloud',
    fields: [['phone_number_id', 'Phone Number ID'], ['access_token', 'Permanent Access Token', true]] },
];

const ProviderCard = ({ def: p, headers, saved, onSaved }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(saved);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/integrations`, {
        method: 'POST', headers,
        body: JSON.stringify({ integration_type: p.integration_type, provider: p.provider, config: {}, credentials: values, is_active: true }),
      });
      if (res.ok) { setOk(true); onSaved && onSaved(); }
    } finally { setSaving(false); }
  };

  const Icon = p.icon;
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-surface-border" data-testid={`integ-card-${p.key}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center"><Icon className="w-4 h-4 text-primary-500" /></div>
          <h2 className="font-display text-lg text-ink">{p.title}</h2>
        </div>
        {ok && <span className="flex items-center gap-1 text-xs text-ok" data-testid={`integ-configured-${p.key}`}><CheckCircle2 className="w-4 h-4" /> Configured</span>}
      </div>
      <div className="space-y-3">
        {p.fields.map(([name, label, secret]) => (
          <div key={name}>
            <label className="block text-xs text-ink-muted mb-1">{label}</label>
            <input type={secret ? 'password' : 'text'} value={values[name] || ''} onChange={e => setValues({ ...values, [name]: e.target.value })}
              data-testid={`integ-${p.key}-${name}`} placeholder={ok ? '•••••• (saved — enter to replace)' : ''}
              className="w-full px-3 py-2 border border-surface-border rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        ))}
        <button onClick={save} disabled={saving} data-testid={`integ-save-${p.key}`}
          className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save keys
        </button>
      </div>
    </div>
  );
};

const IntegrationsSettings = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId } = useOrgScopedHeaders(token);
  const [configured, setConfigured] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/integrations`, { headers });
      const list = res.ok ? await res.json() : [];
      const map = {};
      for (const i of list) map[`${i.integration_type}:${i.provider}`] = true;
      setConfigured(map);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  if (!orgId && !loading) return <div className="max-w-5xl mx-auto p-8 text-ink-muted" data-testid="integ-no-org">No active organization. Please log in.</div>;

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="integrations-settings-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-2 text-xs uppercase tracking-overline text-accent-500">Settings</div>
        <h1 className="font-display text-3xl text-ink mb-2">Messaging & Voice</h1>
        <p className="text-sm text-ink-muted mb-6 max-w-2xl">Add your Twilio and/or Meta credentials to activate live WhatsApp campaigns, DirectConnect ordering, and the VoiceConcierge. Keys are encrypted at rest and never returned to the browser.</p>

        {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PROVIDERS.map(p => (
              <ProviderCard key={p.key} def={p} headers={headers} saved={!!configured[`${p.integration_type}:${p.provider}`]} onSaved={load} />
            ))}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-surface-border flex items-start gap-3" data-testid="integ-voice-webhook">
              <Send className="w-5 h-5 text-primary-500 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-ink mb-1">VoiceConcierge webhook</div>
                <p className="text-ink-muted mb-2">After saving Twilio Voice keys, set your Twilio number's incoming voice webhook (POST) to:</p>
                <code className="block text-xs bg-surface-sunken rounded-md px-3 py-2 break-all">{API_URL}/api/voice/twilio/inbound?orgId={orgId}</code>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsSettings;
